/**
 * CompanyPayrollSettings.js
 *
 * ADDED: Company PAN and TAN fields
 *   • PAN Number — employer PAN, required for Form 16 Part A
 *   • TAN Number — Tax Deduction Account Number, required for Form 16 Part A
 *   • Saved to companies.pan_number and companies.tan_number via PATCH /companies/my
 *   • Validated for correct format (PAN: 10 alphanum, TAN: 10 alphanum)
 *   • Shows warning if missing (Form 16 will be invalid)
 *
 * Existing: Work State + PF Mode + Payslip Generation Day unchanged
 */
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { Shield, Save, Info, Calendar, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

// All 29 Indian states + UTs relevant for payroll
const INDIAN_STATES = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar',
    'Chhattisgarh', 'Delhi', 'Goa', 'Gujarat', 'Haryana',
    'Himachal Pradesh', 'Jammu and Kashmir', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya',
    'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
    'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
];

const ZERO_PT_STATES = [
    'Delhi', 'Uttar Pradesh', 'Rajasthan', 'Haryana', 'Goa',
    'Himachal Pradesh', 'Uttarakhand', 'Jammu and Kashmir',
    'Arunachal Pradesh',
];

const PF_MODES = [
    {
        value:   'capped',
        label:   'Capped at ₹15,000 Basic (Statutory Minimum)',
        detail:  'Employee PF = 12% × min(Basic, ₹15,000). Most common. Compliant for all companies.',
        badge:   'Recommended',
        badgeBg: 'success',
    },
    {
        value:   'actual',
        label:   'On Actual Basic Salary',
        detail:  'Employee PF = 12% × full Basic (even if > ₹15,000). Higher PF contribution.',
        badge:   'Higher PF',
        badgeBg: 'primary',
    },
    {
        value:   'exempt',
        label:   'PF Exempt (No PF deduction)',
        detail:  'Use only if company has an approved PF exemption from EPFO. Consult your CA.',
        badge:   'Special Cases',
        badgeBg: 'warning',
    },
];

// Day options 1–28 (capped at 28 so Feb is always safe)
const DAY_OPTIONS = Array.from({ length: 28 }, (_, i) => i + 1);

const ordinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const buildPreviewDate = (day) => {
    const now      = new Date();
    const month    = now.getMonth();
    const year     = now.getFullYear();
    const payMonth = month === 11 ? 0      : month + 1;
    const payYear  = month === 11 ? year+1 : year;
    const lastDay  = new Date(payYear, payMonth + 1, 0).getDate();
    const finalDay = Math.min(parseInt(day) || 28, lastDay);
    return new Date(payYear, payMonth, finalDay)
        .toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const currentMonthName = () =>
    new Date().toLocaleString('en-IN', { month: 'long', year: 'numeric' });

// PAN format: 5 letters + 4 digits + 1 letter (e.g. ABCDE1234F)
const isValidPAN = (v) => !v || /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(v.toUpperCase());
// TAN format: 4 letters + 5 digits + 1 letter (e.g. DELE12345F)
const isValidTAN = (v) => !v || /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/.test(v.toUpperCase());

// ─────────────────────────────────────────────────────────────────────────────
const CompanyPayrollSettings = () => {
    const [saving,      setSaving]      = useState(false);
    const [loading,     setLoading]     = useState(true);
    const [workState,   setWorkState]   = useState('');
    const [pfMode,      setPfMode]      = useState('capped');
    const [payslipDay,  setPayslipDay]  = useState(28);
    const [panNumber,   setPanNumber]   = useState('');
    const [tanNumber,   setTanNumber]   = useState('');
    const [panTouched,  setPanTouched]  = useState(false);
    const [tanTouched,  setTanTouched]  = useState(false);

    useEffect(() => {
        axiosInstance.get('/companies/my')
            .then(res => {
                const co = res.data.data || {};
                setWorkState(co.work_state               || '');
                setPfMode(co.pf_mode                     || 'capped');
                setPayslipDay(parseInt(co.payslip_generation_day) || 28);
                setPanNumber((co.pan_number              || '').toUpperCase());
                setTanNumber((co.tan_number              || '').toUpperCase());
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const save = async () => {
        // Validate PAN and TAN before saving
        if (panNumber && !isValidPAN(panNumber)) {
            toast.error('Invalid PAN format. Expected: ABCDE1234F (5 letters + 4 digits + 1 letter)');
            return;
        }
        if (tanNumber && !isValidTAN(tanNumber)) {
            toast.error('Invalid TAN format. Expected: DELE12345F (4 letters + 5 digits + 1 letter)');
            return;
        }
        setSaving(true);
        try {
            await axiosInstance.patch('/companies/my', {
                work_state:             workState   || null,
                pf_mode:                pfMode,
                payslip_generation_day: parseInt(payslipDay) || 28,
                pan_number:             panNumber.toUpperCase() || null,
                tan_number:             tanNumber.toUpperCase() || null,
            });
            toast.success('Payroll settings saved! Changes apply from the next payroll run.');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save');
        } finally { setSaving(false); }
    };

    const selectedPfMode = PF_MODES.find(m => m.value === pfMode);
    const isZeroPT       = ZERO_PT_STATES.includes(workState);
    const previewDate    = buildPreviewDate(payslipDay);
    const panValid       = isValidPAN(panNumber);
    const tanValid       = isValidTAN(tanNumber);
    const form16Ready    = !!panNumber && !!tanNumber && panValid && tanValid;

    if (loading) return (
        <div className="text-center py-3">
            <Spinner animation="border" size="sm"/>
        </div>
    );

    return (
        <Card className="border-0 shadow-sm mt-4">
            <Card.Header className="bg-white border-0 py-3">
                <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <Shield size={18} className="text-primary"/>
                    Payroll &amp; Tax Settings
                </h6>
                <p className="text-muted small mb-0 mt-1">
                    Configure state-based Professional Tax, PF deduction mode,
                    salary payment day, and company PAN/TAN for Form 16 generation.
                </p>
            </Card.Header>
            <Card.Body className="p-4">
                <Row className="g-4">

                    {/* ── Company PAN + TAN ─────────────────────────────────── */}
                    <Col xs={12}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <FileText size={16} className="text-primary"/>
                            <span className="fw-bold small">Form 16 Details — Company PAN &amp; TAN</span>
                            {form16Ready
                                ? <Badge bg="success" style={{ fontSize: 10 }}>✓ Form 16 Ready</Badge>
                                : <Badge bg="warning" text="dark" style={{ fontSize: 10 }}>⚠ Required for Form 16</Badge>
                            }
                        </div>
                        <Row className="g-3">
                            <Col xs={12} md={6}>
                                <Form.Label className="fw-bold small">
                                    Company PAN Number
                                    <span className="text-danger ms-1">*</span>
                                    <span className="text-muted fw-normal ms-1">(for Form 16 Part A)</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="ABCDE1234F"
                                    value={panNumber}
                                    maxLength={10}
                                    style={{
                                        borderRadius:    8,
                                        fontFamily:      'monospace',
                                        textTransform:   'uppercase',
                                        letterSpacing:   '1px',
                                        borderColor:     panTouched && panNumber && !panValid ? '#ef4444' :
                                                         panTouched && panNumber && panValid  ? '#059669' : undefined,
                                    }}
                                    onChange={e => setPanNumber(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                                    onBlur={() => setPanTouched(true)}
                                    isInvalid={panTouched && !!panNumber && !panValid}
                                    isValid={panTouched && !!panNumber && panValid}
                                />
                                <Form.Control.Feedback type="invalid">
                                    Invalid PAN. Format: ABCDE1234F (5 letters + 4 digits + 1 letter)
                                </Form.Control.Feedback>
                                {!panNumber && (
                                    <Form.Text className="text-warning">
                                        ⚠ PAN is mandatory for Form 16. Employees cannot file ITR without this.
                                    </Form.Text>
                                )}
                                {panNumber && panValid && (
                                    <Form.Text className="text-success">✓ Valid PAN format</Form.Text>
                                )}
                            </Col>
                            <Col xs={12} md={6}>
                                <Form.Label className="fw-bold small">
                                    Company TAN Number
                                    <span className="text-danger ms-1">*</span>
                                    <span className="text-muted fw-normal ms-1">(Tax Deduction Account No.)</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="DELE12345F"
                                    value={tanNumber}
                                    maxLength={10}
                                    style={{
                                        borderRadius:  8,
                                        fontFamily:    'monospace',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px',
                                        borderColor:   tanTouched && tanNumber && !tanValid ? '#ef4444' :
                                                       tanTouched && tanNumber && tanValid  ? '#059669' : undefined,
                                    }}
                                    onChange={e => setTanNumber(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                                    onBlur={() => setTanTouched(true)}
                                    isInvalid={tanTouched && !!tanNumber && !tanValid}
                                    isValid={tanTouched && !!tanNumber && tanValid}
                                />
                                <Form.Control.Feedback type="invalid">
                                    Invalid TAN. Format: DELE12345F (4 letters + 5 digits + 1 letter)
                                </Form.Control.Feedback>
                                {!tanNumber && (
                                    <Form.Text className="text-warning">
                                        ⚠ TAN required — obtained from Income Tax Dept when registering as TDS deductor.
                                    </Form.Text>
                                )}
                                {tanNumber && tanValid && (
                                    <Form.Text className="text-success">✓ Valid TAN format</Form.Text>
                                )}
                            </Col>
                        </Row>
                        <div className="mt-2 p-3 rounded small"
                            style={{ background: form16Ready ? '#f0fdf4' : '#fffbeb', border: `1px solid ${form16Ready ? '#86efac' : '#fcd34d'}` }}>
                            {form16Ready ? (
                                <span className="text-success">
                                    ✅ <strong>Form 16 is ready to generate.</strong> Company PAN and TAN are configured.
                                    Go to <strong>Payroll → Form 16</strong> tab to download Form 16 for employees.
                                </span>
                            ) : (
                                <span className="text-warning-emphasis">
                                    ⚠ <strong>Form 16 cannot be generated</strong> until both PAN and TAN are entered.
                                    These are required by the Income Tax Department for TDS certificates.
                                    Contact your CA or accountant if you don't have these.
                                </span>
                            )}
                        </div>
                    </Col>

                    {/* ── Work State ─────────────────────────────────────────── */}
                    <Col xs={12} md={6}>
                        <Form.Label className="fw-bold small">
                            Company Work State
                            <span className="text-muted fw-normal ms-1">(for Professional Tax)</span>
                        </Form.Label>
                        <Form.Select
                            value={workState}
                            onChange={e => setWorkState(e.target.value)}
                            style={{ borderRadius: 8 }}>
                            <option value="">— Select State —</option>
                            {INDIAN_STATES.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </Form.Select>
                        {workState ? (
                            <div className="mt-2 p-2 rounded small"
                                style={{ background: isZeroPT ? '#f0fdf4' : '#f0f4ff' }}>
                                {isZeroPT ? (
                                    <span className="text-success">
                                        ✅ <strong>{workState}</strong> has no Professional Tax.
                                        PT deduction will be ₹0 for all employees.
                                    </span>
                                ) : (
                                    <span className="text-primary">
                                        🏛️ <strong>{workState}</strong> levies Professional Tax.
                                        Slabs applied automatically based on gross salary.
                                    </span>
                                )}
                            </div>
                        ) : (
                            <Alert variant="warning" className="small py-2 mt-2">
                                <Info size={13} className="me-1"/>
                                No state selected — PT defaults to ₹0.
                                Set this to apply correct state PT.
                            </Alert>
                        )}
                    </Col>

                    {/* ── Payslip Generation Day ─────────────────────────────── */}
                    <Col xs={12} md={6}>
                        <Form.Label className="fw-bold small d-flex align-items-center gap-2">
                            <Calendar size={15} className="text-primary"/>
                            Salary Payment Day
                            <Badge bg="info" style={{ fontSize: 9 }}>New</Badge>
                        </Form.Label>
                        <Form.Select
                            value={payslipDay}
                            onChange={e => setPayslipDay(parseInt(e.target.value))}
                            style={{ borderRadius: 8 }}>
                            {DAY_OPTIONS.map(d => (
                                <option key={d} value={d}>
                                    {ordinal(d)} of the month
                                    {d === 1  ? ' (1st — early credit)'       : ''}
                                    {d === 5  ? ' (5th — common)'             : ''}
                                    {d === 10 ? ' (10th — common)'            : ''}
                                    {d === 28 ? ' (28th — default, Feb-safe)' : ''}
                                </option>
                            ))}
                        </Form.Select>
                        <div className="mt-2 p-2 rounded small" style={{ background: '#f0f4ff' }}>
                            <span className="text-primary">
                                💳 Salaries for <strong>{currentMonthName()}</strong> will show
                                payment date: <strong>{previewDate}</strong>
                            </span>
                        </div>
                        <Form.Text className="text-muted">
                            Capped at 28 — safe for all months including February.
                            This sets the <code>payment_date</code> in payroll records.
                        </Form.Text>
                    </Col>

                    {/* ── PF Mode ────────────────────────────────────────────── */}
                    <Col xs={12}>
                        <Form.Label className="fw-bold small">PF Calculation Mode</Form.Label>
                        <Row className="g-2">
                            {PF_MODES.map(mode => (
                                <Col key={mode.value} xs={12} md={4}>
                                    <div
                                        onClick={() => setPfMode(mode.value)}
                                        style={{
                                            padding:      '10px 14px',
                                            borderRadius: 8,
                                            border:       pfMode === mode.value
                                                              ? '2px solid #4f46e5'
                                                              : '1px solid #e5e7eb',
                                            background:   pfMode === mode.value ? '#f0f4ff' : '#fff',
                                            cursor:       'pointer',
                                            transition:   'all 0.15s',
                                            height:       '100%',
                                        }}>
                                        <div className="d-flex align-items-center gap-2">
                                            <Form.Check
                                                type="radio"
                                                checked={pfMode === mode.value}
                                                onChange={() => setPfMode(mode.value)}
                                                className="mb-0"
                                            />
                                            <span className="fw-bold small">{mode.label}</span>
                                            <Badge bg={mode.badgeBg} style={{ fontSize: 9 }}>
                                                {mode.badge}
                                            </Badge>
                                        </div>
                                        <div className="text-muted"
                                            style={{ fontSize: 11, marginLeft: 24, marginTop: 4 }}>
                                            {mode.detail}
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                        {selectedPfMode && (
                            <div className="mt-2 p-2 rounded small bg-light">
                                <strong>Example (₹50,000 Basic):</strong>
                                {pfMode === 'capped' && ' PF = 12% × ₹15,000 = ₹1,800/month'}
                                {pfMode === 'actual' && ' PF = 12% × ₹50,000 = ₹6,000/month'}
                                {pfMode === 'exempt' && ' PF = ₹0/month (exempt)'}
                            </div>
                        )}
                    </Col>

                    {/* ── Info note ──────────────────────────────────────────── */}
                    <Col xs={12}>
                        <Alert variant="info" className="small py-2 mb-0">
                            <Info size={13} className="me-1"/>
                            <strong>Employee-level override:</strong> Individual employees can have
                            a different work state (remote workers) or PF opt-out — set in the
                            employee profile. Employee PAN is set per-employee in the employee edit form.
                            Employee settings take priority over company defaults.
                        </Alert>
                    </Col>
                </Row>

                <div className="mt-4">
                    <Button
                        variant="primary"
                        onClick={save}
                        disabled={saving}
                        className="d-flex align-items-center gap-2">
                        {saving ? <Spinner size="sm"/> : <Save size={14}/>}
                        Save Payroll Settings
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};

export default CompanyPayrollSettings;
